import { useState } from "react";
import projectData from "../data/projectData.json";
import "../styles/HomeNew.css";

const EVENTS = [
  {
    name: "Kite Fest",
    href: "https://www.forbettertomorrow.org/kitefest/",
    image: "/images/projects/kite-fest-150x150.png",
    emoji: "🪁",
    color: "card--purple",
    label: "Annual Fundraiser",
    desc: "Family fundraiser with live music, dance, face painting, and kite flying. Raises funds for local and global nonprofits.",
  },
  {
    name: "YICU Service Awards",
    href: "https://www.forbettertomorrow.org/yicu/",
    image: "/images/YICU/2025/Amanda Hapgood2.jpg",
    emoji: "🏆",
    color: "card--pink",
    label: "Service Banquet",
    desc: "Celebrating young adults who demonstrate leadership, service, and a commitment to strengthening the community.",
  },
  {
    name: "Youth Global Citizen",
    href: "https://www.forbettertomorrow.org/ygc/",
    image: "/images/ygc/2025/YGC2025.jpg",
    emoji: "🌱",
    color: "card--green",
    label: "Leadership Program",
    desc: "An immersive leadership experience for high school students focused on self-awareness, service, philanthropy, and global citizenship.",
  },
  {
    name: "Run 4 FBT",
    href: "https://www.forbettertomorrow.org/5k",
    image: "/images/homePage/5k_home.jpg",
    emoji: "🏃",
    color: "card--purple",
    label: "Running Event",
    desc: "Run with purpose. Move for your community. This unique 5K blends fitness with hands-on experiences along the course—engaging participants in STEAM activities while supporting youth experiencing homelessness through Project Oz.",
  },
  {
    name: "STEAM Olympics",
    href: "https://www.forbettertomorrow.org/steam",
    image: "/images/steam/2024/SF-JH-BE.png",
    emoji: "🔬",
    color: "card--pink",
    label: "STEAM Competition",
    desc: "A dynamic individual and team-based competition that brings STEAM to life through hands-on challenges, creativity, and collaboration—empowering students to think critically, solve real-world problems, and showcase their skills.",
  },
  {
    name: "Youth Volunteer Corps (YVC)",
    href: "https://www.forbettertomorrow.org/yvc/",
    image: "/images/YVC/Midwest-January.png",
    emoji: "🌍",
    color: "card--green",
    label: "Civic Engagement",
    desc: "An individual- and team-based service experience that empowers youth to give back through hands-on volunteering, leadership development, and meaningful civic engagement.",
  },
];

const PILLARS = [
  { label: "Accountability", color: "pill--purple", href: "https://www.forbettertomorrow.org/about/" },
  { label: "Sustainable Philanthropy", color: "pill--pink", href: "https://www.forbettertomorrow.org/about/" },
  { label: "Nurturing Future Generations", color: "pill--green", href: "https://www.forbettertomorrow.org/about/" },
];

const G_META = {
  "solidarity-engineering": { focus: "Clean Water / Health", region: "Americas", color: "dot--purple" },
  "action-in-africa": { focus: "Education", region: "Uganda", color: "dot--pink" },
  "mali-rising": { focus: "Girls' Education", region: "West Africa", color: "dot--green" },
  "connected": { focus: "Education Access", region: "Central America", color: "dot--purple" },
  "el-porvenir": { focus: "Clean Water", region: "Nicaragua", color: "dot--pink" },
  "maya-book-project": { focus: "Literacy", region: "Guatemala", color: "dot--green" },
  "safe-passage": { focus: "Health / Education", region: "Guatemala", color: "dot--purple" },
  "yabal": { focus: "Women Empowerment", region: "Guatemala", color: "dot--pink" },
  "ihf": { focus: "Child Education", region: "Guatemala", color: "dot--green" },
  "kyc": { focus: "Youth Health", region: "Kenya", color: "dot--purple" },
};

const L_META = {
  "b2s": { focus: "Education", color: "lchip--purple" },
  "one-hope-project": { focus: "Health", color: "lchip--pink" },
  "ywca": { focus: "Equity", color: "lchip--green" },
  "creative-healing": { focus: "Mental Health", color: "lchip--purple" },
  "bgc": { focus: "Youth", color: "lchip--pink" },
  "art-station": { focus: "Arts", color: "lchip--green" },
  "sunnyside-community-garden": { focus: "Food Security", color: "lchip--purple" },
  "charm": { focus: "Community", color: "lchip--pink" },
  "project-oz": { focus: "Youth Services", color: "lchip--green" },
  "dreams-are-possible": { focus: "Community", color: "lchip--purple" },
  "path": { focus: "Crisis Support", color: "lchip--pink" },
};

function GlobalCard({ proj }) {
  const [open, setOpen] = useState(false);
  const m = G_META[proj.uriName] || { focus: "Philanthropy", region: "Global", color: "dot--purple" };
  return (
    <div className={`gcard${open ? " gcard--open" : ""}`}>
      <div className="gcard__top">
        <span className={`gcard__dot ${m.color}`}>{m.focus}</span>
        <div className="gcard__region">{m.region}</div>
      </div>
      <h3 className="gcard__name">{proj.name}</h3>
      <p className="gcard__desc">{proj.shortDesc}</p>
      {open && (
        <div className="gcard__extra">
          {proj.desc !== proj.shortDesc && (
            <p dangerouslySetInnerHTML={{ __html: proj.desc }} />
          )}
          <a href={proj.uri} target="_blank" rel="noopener noreferrer">Visit website ↗</a>
        </div>
      )}
      <button className="gcard__toggle" onClick={() => setOpen(v => !v)}>
        {open ? "Show less ↑" : "Read more ↓"}
      </button>
    </div>
  );
}

function LocalCard({ proj }) {
  const [open, setOpen] = useState(false);
  const m = L_META[proj.uriName] || { focus: "Community", color: "lchip--purple" };
  return (
    <div className={`lcard${open ? " lcard--open" : ""}`}>
      <div className="lcard__top">
        <span className={`lcard__chip ${m.color}`}>{m.focus}</span>
        <div className="lcard__logo">
          <img src={`/images/projects/${proj.logo}`} alt="" loading="lazy"
            onError={e => e.target.style.display = "none"} />
        </div>
      </div>
      <h3 className="lcard__name">{proj.name}</h3>
      <p className="lcard__desc">{proj.shortDesc}</p>
      {open && (
        <div className="lcard__extra">
          {proj.desc !== proj.shortDesc && (
            <p dangerouslySetInnerHTML={{ __html: proj.desc }} />
          )}
          <a href={proj.uri} target="_blank" rel="noopener noreferrer">Visit website ↗</a>
        </div>
      )}
      <button className="lcard__toggle" onClick={() => setOpen(v => !v)}>
        {open ? "Show less ↑" : "Read more ↓"}
      </button>
    </div>
  );
}

export default function Home() {
  const global = projectData.projects.filter(p => p.type === "G");
  const local = projectData.projects.filter(p => p.type === "L");

  return (
    <div className="pg">




      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__glow hero__glow--1" />
        <div className="hero__glow hero__glow--2" />
        <div className="hero__glow hero__glow--3" />
        <div className="hero__inner">
          <div className="hero__text">
            <p className="hero__kicker">For A Better Tomorrow</p>
            <h1 className="hero__h1">
              Bridging<br />
              <span className="hero__accent">Abundance</span><br />
              &amp; Need
            </h1>
            <p className="hero__sub">
              FBT promotes social justice by meeting needs in the local community
              and the world through accountable, sustainable philanthropy — and
              by nurturing future generations of servant leaders.
            </p>
            <div className="hero__pills">
              {PILLARS.map(p => (
                <a key={p.label} href={p.href} className={`pill ${p.color}`}>{p.label}</a>
              ))}
            </div>
            <div className="hero__ctas">
              <a href="https://www.forbettertomorrow.org/about/" className="cta cta--solid">Our Mission</a>
              <a href="#events" className="cta cta--ghost">See Programs ↓</a>
            </div>
          </div>
          <div className="hero__stats">
            <div className="hstat hstat--purple">
              <span className="hstat__n">{global.length}</span>
              <span className="hstat__l">Global Partners</span>
            </div>
            <div className="hstat hstat--pink">
              <span className="hstat__n">{local.length}</span>
              <span className="hstat__l">Local Partners</span>
            </div>
            <div className="hstat hstat--green">
              <span className="hstat__n">4</span>
              <span className="hstat__l">Annual Events</span>
            </div>
            <div className="hstat hstat--soft">
              <span className="hstat__n">IL</span>
              <span className="hstat__l">Based in Illinois</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── EVENTS ── */}
      <section id="events" className="events">
        <div className="sec-hd">
          <span className="sec-eye">Programs &amp; Events</span>
          <h2 className="sec-h2">Advancing the Mission</h2>
          <p className="sec-sub">FBT programs that create lasting community impact year after year.</p>
        </div>
        <div className="events__grid">
          {EVENTS.map(ev => (
            <a key={ev.name} href={ev.href} className={`ecard ${ev.color}`}>
              <div className="ecard__icon">{ev.emoji}</div>
              <div className="ecard__img">
                <img src={ev.image} alt={ev.name} loading="lazy" />
              </div>
              <span className="ecard__label">{ev.label}</span>
              <h3 className="ecard__name">{ev.name}</h3>
              <p className="ecard__desc">{ev.desc}</p>
              <span className="ecard__arrow">Learn more →</span>
            </a>
          ))}
        </div>
      </section>

      {/* ── GLOBAL PROJECTS ── */}
      <section id="global" className="global">
        <div className="sec-hd">
          <span className="sec-eye">Around the World</span>
          <h2 className="sec-h2">Global Projects</h2>
          <p className="sec-sub">
            FBT-funded initiatives spanning education, water, health, and economic development worldwide.
          </p>
        </div>
        <div className="global__grid">
          {global.map(p => <GlobalCard key={p.uriName} proj={p} />)}
        </div>
      </section>

      {/* ── QUOTE ── */}
      <div className="quote-band">
        <p>"FBT believes in hope — and hope for a better tomorrow."</p>
      </div>

      {/* ── LOCAL PROJECTS ── */}
      <section className="local">
        <div className="sec-hd">
          <span className="sec-eye">Close to Home</span>
          <h2 className="sec-h2">Local Projects</h2>
          <p className="sec-sub">Supporting McLean County and Central Illinois communities.</p>
        </div>
        <div className="local__grid">
          {local.map(p => <LocalCard key={p.uriName} proj={p} />)}
        </div>
      </section>



    </div>
  );
}